import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LocationAutocomplete from '../../components/LocationAutocomplete';
import InfoPage from '../InfoPage';
import IntroVideoUpload from '../../components/IntroVideoUpload';
import ShowcaseUpload from '../../components/ShowcaseUpload';
import './PropertyEditor.sass';
import { uploadFile } from '../../utils/fileUpload';
import { getPropertyLabel, getPropertyLabelLower } from '../../utils/labelHelpers';

const AMENITIES = [
  { key: 'hot_tub', label: 'Hot Tub' },
  { key: 'swimming_pool', label: 'Pool' },
  { key: 'sauna', label: 'Sauna' },
  { key: 'cold_plunge', label: 'Cold Plunge' },
  { key: 'firepit', label: 'Fire Pit' },
  { key: 'outdoor_deck', label: 'Outdoor Deck' },
  { key: 'lake', label: 'Lake Access' },
  { key: 'mountains', label: 'Mountain Views' },
  { key: 'beach', label: 'Beach Access' },
  { key: 'golf', label: 'Golf Course' },
  { key: 'skiing', label: 'Ski Access' },
  { key: 'pet_friendly', label: 'Pet Friendly' },
  { key: 'family_friendly', label: 'Family Friendly' },
  { key: 'free_parking', label: 'Free Parking' },
  { key: 'ev_charger', label: 'EV Charger' },
  { key: 'grill', label: 'BBQ Grill' },
  { key: 'smartlock', label: 'Smart Lock' },
  { key: 'low_toxin', label: 'Low Toxin' },
  { key: 'accessible', label: 'Wheelchair Accessible' },
  { key: 'river_access', label: 'River Access' },
  { key: 'pickleball', label: 'Access to Pickleball' },
  { key: 'tennis_courts', label: 'Access to Tennis Courts' }
];

// Unified property types across Vacation Rentals and Boutique Hotels
const PROPERTY_TYPES = [
  // Vacation rental common types
  { value: 'single_family_home', label: 'Single Family Home' },
  { value: 'condo', label: 'Condo' },
  { value: 'townhome', label: 'Townhome' },
  { value: 'tiny_home', label: 'Tiny Home' },
  { value: 'cottage', label: 'Cottage' },
  { value: 'guesthouse', label: 'Guest House' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'villa', label: 'Villa' },
  { value: 'cabin', label: 'Cabin' },
  { value: 'a_frame', label: 'A-Frame' },
  // Boutique hotel types
  { value: 'boutique', label: 'Boutique' },
  { value: 'resort', label: 'Resort' },
  { value: 'motel', label: 'Motel' },
  { value: 'micro_hotel', label: 'Micro-Hotel' },
  { value: 'wellness', label: 'Wellness' },
  { value: 'glamping', label: 'Glamping' },
  { value: 'floating', label: 'Floating' },
  { value: 'igloo', label: 'Igloo' },
  // Fallback
  { value: 'other', label: 'Other' }
];

const PropertyEditor = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const propertyId = searchParams.get('id') || null;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);
  const [propertyLimits, setPropertyLimits] = useState(null);
  const [showLimitWarning, setShowLimitWarning] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [hostCategory, setHostCategory] = useState(null);
  
  // Conditional labels based on host category
  const propertyLabel = getPropertyLabel(hostCategory);
  const propertiesLabel = getPropertyLabel(hostCategory, true);
  const propertyLabelLower = getPropertyLabelLower(hostCategory);
  const propertiesLabelLower = getPropertyLabelLower(hostCategory, true);
  
  // Property fields
  const [name, setName] = useState('');
  const [propertyHandle, setPropertyHandle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('draft');
  const [featured, setFeatured] = useState(false);
  const [locationCountry, setLocationCountry] = useState('');
  const [locationCity, setLocationCity] = useState('');
  const [locationState, setLocationState] = useState('');
  const [propertyType, setPropertyType] = useState('single_family_home');
  const [bedrooms, setBedrooms] = useState('');
  const [bathrooms, setBathrooms] = useState('');
  const [maxGuests, setMaxGuests] = useState('');
  const [sleeps, setSleeps] = useState('');
  const [squareFootage, setSquareFootage] = useState('');
  const [amenities, setAmenities] = useState([]);
  
  
  // Location details
  const [nearestAirport, setNearestAirport] = useState('');
  const [drivingDistanceMiles, setDrivingDistanceMiles] = useState('');
  const [drivingTimeMinutes, setDrivingTimeMinutes] = useState('');
  
  // Listing URLs
  const [directBookingUrl, setDirectBookingUrl] = useState('');
  const [airbnbUrl, setAirbnbUrl] = useState('');
  const [vrboUrl, setVrboUrl] = useState('');
  const [otherListingUrl, setOtherListingUrl] = useState('');
  const [expediaUrl, setExpediaUrl] = useState('');
  
  // Social Links
  const [igUrl, setIgUrl] = useState('');
  const [ttUrl, setTtUrl] = useState('');
  const [ytUrl, setYtUrl] = useState('');
  const [ltkUrl, setLtkUrl] = useState('');
  
  // Image
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadSuccess, setImageUploadSuccess] = useState(false);
  const fileInputRef = useRef(null);

  // Intro video (per-property)
  const [introVideo, setIntroVideo] = useState(null);
  const [introVideoStatus, setIntroVideoStatus] = useState('');

  // Showcase (per-property)
  const [showcaseAssets, setShowcaseAssets] = useState([]);

  // Add subscription check state
  const [authorized, setAuthorized] = useState(null);

  // Check subscription and host role before allowing access
  useEffect(() => {
    if (!email) {
      // Email check already handled below, but ensure we don't check subscription without email
      return;
    }

    (async () => {
      try {
        const res = await fetch('/api/access/subscription', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });
        const data = await res.json();

        if (res.ok && data.ok && data.active && (data.roles || []).includes('host')) {
          setAuthorized(true);
          
          // Fetch host profile to get host_category
          try {
            const apiBase = window.location.hostname === 'localhost' ? 
              'http://localhost:4000' : '';
            const profileResponse = await fetch(`${apiBase}/api/host/profile?email=${encodeURIComponent(email)}`);
            const profileData = await profileResponse.json();
            if (profileData.ok && profileData.profile) {
              setHostCategory(profileData.profile.host_category);
            }
          } catch (profileErr) {
            console.error('Failed to fetch host profile:', profileErr);
          }
        } else {
          // Redirect to home if no active subscription or not a host
          navigate('/');
        }
      } catch (err) {
        // Redirect to home on error
        navigate('/');
      }
    })();
  }, [email, navigate]);

  // Load existing property if editing (only if authorized)
  useEffect(() => {
    if (propertyId && authorized === true) {
      loadProperty();
    }
  }, [propertyId, authorized]);

  // Load per-property showcase assets when editing an existing property.
  useEffect(() => {
    if (!propertyId || authorized !== true) {
      setShowcaseAssets([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const apiBase = window.location.hostname === 'localhost' ?
          'http://localhost:4000' : '';
        const res = await fetch(
          `${apiBase}/api/showcase?profile_kind=property&property_id=${encodeURIComponent(propertyId)}`
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.ok) {
          setShowcaseAssets(Array.isArray(data.assets) ? data.assets : []);
        }
      } catch (err) {
        console.error('Failed to fetch property showcase assets:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [propertyId, authorized]);

  const loadProperty = async () => {
    setLoading(true);
    try {
      const apiBase = window.location.hostname === 'localhost' ? 
        'http://localhost:4000' : '';
      
      const response = await fetch(`${apiBase}/api/properties/list?email=${encodeURIComponent(email)}`);
      const data = await response.json();
      
      if (data.ok) {
        // Store property limits
        if (data.propertyLimits) {
          setPropertyLimits(data.propertyLimits);
          
          // Check if creating new property and at limit
          if (!propertyId && data.propertyLimits.currentCount >= data.propertyLimits.maxProperties) {
            setShowLimitWarning(true);
          }
        }
        
        const property = data.properties.find(p => p.id === propertyId);
        if (property) {
          setName(property.name || '');
          setDescription(property.description || '');
          setPropertyHandle((property.slug || '').replace(/^@/, ''));
          setStatus(property.status || 'draft');
          setFeatured(property.featured || false);
          setLocationCountry(property.location_country || '');
          setLocationCity(property.location_city || '');
          setLocationState(property.location_state || '');
          setPropertyType(property.property_type || 'single_family_home');
          setBedrooms(property.bedrooms?.toString() || '');
          setBathrooms(property.bathrooms?.toString() || '');
          setMaxGuests(property.max_guests?.toString() || '');
          setSleeps(property.sleeps?.toString() || '');
          setSquareFootage(property.square_footage?.toString() || '');
          setAmenities(property.amenities || []);
          setNearestAirport(property.nearest_airport || '');
          setDrivingDistanceMiles(property.driving_distance_miles?.toString() || '');
          setDrivingTimeMinutes(property.driving_time_minutes?.toString() || '');
          setDirectBookingUrl(property.direct_booking_url || '');
          setAirbnbUrl(property.airbnb_url || '');
          setVrboUrl(property.vrbo_url || '');
          setOtherListingUrl(property.other_listing_url || '');
          setExpediaUrl(property.expedia_url || '');

          // Socials may be stored as { platform: { url } } or { platform: 'url' }
          const socials = property.socials || {};
          const getUrl = (entry) => (typeof entry === 'string' ? entry : (entry && entry.url) || '');
          setIgUrl(getUrl(socials.instagram));
          setTtUrl(getUrl(socials.tiktok));
          setYtUrl(getUrl(socials.youtube));
          setLtkUrl(getUrl(socials.ltk));
          setImageUrl(property.main_image_url || property.image_url || '');
          setImagePreview(property.main_image_url || property.image_url || '');
          if (property.intro_video_url || property.intro_video_public_id) {
            setIntroVideo({
              url: property.intro_video_url || null,
              posterUrl: property.intro_video_poster_url || null,
              hlsUrl: property.intro_video_hls_url || null,
              publicId: property.intro_video_public_id || null,
              duration: property.intro_video_duration_sec || null,
            });
          } else {
            setIntroVideo(null);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load property:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to trigger file input with cross-browser support
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      // Use showPicker() if available (Chrome 99+), fallback to click()
      if ('showPicker' in HTMLInputElement.prototype) {
        try {
          fileInputRef.current.showPicker();
        } catch (err) {
          // If showPicker fails, fall back to click
          fileInputRef.current.click();
        }
      } else {
        // Fallback for older browsers
        fileInputRef.current.click();
      }
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImageUploading(true);
      setImageUploadSuccess(false);
      setImagePreview('');
      setSaveError(null);
      
      try {
        // Upload file directly (handles HEIC conversion)
        const url = await uploadFile(file);
        setImageUrl(url);
        setImagePreview(url);
        setImageUploadSuccess(true);
        
        // Hide checkmark after 2 seconds
        setTimeout(() => setImageUploadSuccess(false), 2000);
      } catch (error) {
        console.error('Failed to upload image:', error);
        setSaveError('Failed to upload image. Please try again with a JPG, PNG, or HEIC photo.');
      } finally {
        setImageUploading(false);
      }
    }
  };

  const uploadImage = async () => {
    // Image already uploaded in handleImageChange
    return imageUrl || imagePreview;
  };

  const handleIntroVideoChange = async (nextVideo) => {
    setIntroVideo(nextVideo);
    if (!propertyId) {
      // Without a saved property we can't persist; the upload component will surface a hint.
      return;
    }
    setIntroVideoStatus(nextVideo ? 'Saving intro video...' : 'Removing intro video...');
    try {
      const apiBase = window.location.hostname === 'localhost' ?
        'http://localhost:4000' : '';
      const response = await fetch(`${apiBase}/api/properties/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          property_id: propertyId,
          property: { intro_video: nextVideo },
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.ok) {
        throw new Error(data.error || 'save_failed');
      }
      setIntroVideoStatus(nextVideo ? 'Intro video saved.' : 'Intro video removed.');
    } catch (err) {
      console.error('Failed to save property intro video:', err);
      setIntroVideoStatus('Could not save intro video. Please try again.');
    } finally {
      setTimeout(() => setIntroVideoStatus(''), 3000);
    }
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    
    // Clear any previous errors
    setSaveError(null);
    
    if (!name.trim()) {
      setSaveError(`${propertyLabel} name is required`);
      // Scroll to name field
      setTimeout(() => {
        const nameInput = document.querySelector('input[type="text"][placeholder*="Sunset"]');
        if (nameInput) {
          nameInput.focus();
          nameInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

    setSaving(true);
    
    try {
      const uploadedImageUrl = await uploadImage();
      
      const sanitizedHandle = propertyHandle
        ? propertyHandle.toLowerCase().replace(/[^a-z0-9._-]/g, '')
        : '';

      const propertyData = {
        name: name.trim(),
        slug: sanitizedHandle || null,
        description,
        status,
        featured,
        location_country: locationCountry,
        location_city: locationCity,
        location_state: locationState,
        property_type: propertyType,
        bedrooms: bedrooms ? parseFloat(bedrooms) : null,
        bathrooms: bathrooms ? parseFloat(bathrooms) : null,
        max_guests: maxGuests ? parseFloat(maxGuests) : null,
        sleeps: sleeps ? parseFloat(sleeps) : null,
        square_footage: squareFootage ? parseFloat(squareFootage) : null,
        amenities,
        nearest_airport: nearestAirport,
        driving_distance_miles: drivingDistanceMiles ? parseFloat(drivingDistanceMiles) : null,
        driving_time_minutes: drivingTimeMinutes ? parseInt(drivingTimeMinutes) : null,
        direct_booking_url: directBookingUrl,
        airbnb_url: airbnbUrl,
        vrbo_url: vrboUrl,
        other_listing_url: otherListingUrl,
        expedia_url: expediaUrl,
        socials: {
          instagram: igUrl ? { url: igUrl } : null,
          tiktok: ttUrl ? { url: ttUrl } : null,
          youtube: ytUrl ? { url: ytUrl } : null,
          ltk: ltkUrl ? { url: ltkUrl } : null,
        },
        main_image_url: uploadedImageUrl
      };

      const apiBase = window.location.hostname === 'localhost' ? 
        'http://localhost:4000' : '';
      
      const endpoint = propertyId ? '/api/properties/update' : '/api/properties/create';
      const body = propertyId ? 
        { email, property_id: propertyId, property: propertyData } :
        { email, property: propertyData };

      const response = await fetch(`${apiBase}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      
      if (data.ok) {
        // Clear error on success
        setSaveError(null);
        navigate(`/property-management?email=${encodeURIComponent(email)}`);
      } else {
        // Handle property limit error specifically
        if (data.error === 'property_limit_reached') {
          const message = data.message || `You've reached your property limit of ${data.maxProperties} properties.`;
          const confirmUpgrade = window.confirm(
            `${message}\n\nWould you like to upgrade your plan?`
          );
          if (confirmUpgrade) {
            let plan = '';
            if (data.maxProperties <= 3) {
              plan = 'passport';
            } else if (data.maxProperties <= 10) {
              plan = 'visa';
            }
            navigate(`/host-checkout${plan ? `?plan=${plan}` : ''}`);
          }
        } else if (data.error === 'slug_already_exists') {
          // Handle slug conflict with user-friendly message
          setSaveError(data.message || 'The property handle is already taken. Please choose a different handle.');
          // Scroll to handle field
          setTimeout(() => {
            const handleInput = document.querySelector('input[type="text"][placeholder*="yourproperty"]');
            if (handleInput) {
              handleInput.focus();
              handleInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        } else {
          // Set inline error message
          const errorMessage = data.error || data.message || 'Unknown error occurred';
          setSaveError(`Failed to save property: ${errorMessage}`);
          console.error('Save error details:', data);
        }
      }
    } catch (error) {
      console.error('Failed to save property:', error);
      // Set inline error message
      setSaveError(`Failed to save property: ${error.message || 'Network error. Please check your connection and try again.'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAmenityToggle = (amenityKey) => {
    setAmenities(prev => 
      prev.includes(amenityKey)
        ? prev.filter(a => a !== amenityKey)
        : [...prev, amenityKey]
    );
  };

  if (!email) {
    return (
      <InfoPage
        title={`${propertyLabel} Editor`}
        below={
          <div className="property-editor-full-width">
            <div className="property-editor">
              <div className="property-editor__error">
                <h2>Please sign in to manage {propertiesLabelLower}</h2>
                <button onClick={() => navigate('/')} className="btn btn--primary">
                  Go to Home
                </button>
              </div>
            </div>
          </div>
        }
        tallOverride={false}
        customMinHeight={400}
        disableStamps
      />
    );
  }

  // Add authorization check
  if (authorized === null) {
    return (
      <InfoPage
        title={`${propertyLabel} Editor`}
        below={
          <div className="property-editor-full-width">
            <div className="property-editor">
              <div className="property-editor__loading">Loading...</div>
            </div>
          </div>
        }
        tallOverride={false}
        customMinHeight={600}
        disableStamps
      />
    );
  }

  if (authorized === false) {
    return null; // navigate will handle redirect
  }

  if (loading) {
    return (
      <InfoPage
        title={propertyId ? `Edit ${propertyLabel}` : `Add New ${propertyLabel}`}
        below={
          <div className="property-editor-full-width">
            <div className="property-editor">
              <div className="property-editor__loading">Loading property...</div>
            </div>
          </div>
        }
        tallOverride={false}
        customMinHeight={600}
        disableStamps
      />
    );
  }

  const editorContent = (
    <div className="property-editor-full-width">
      <div className="property-editor">
      {showLimitWarning && propertyLimits && (
        <div style={{
          backgroundColor: '#FEF2F2',
          border: '1px solid #FCA5A5',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <span style={{ fontSize: '16px' }}>⚠️</span>
              <strong style={{ color: '#DC2626' }}>{propertyLabel} Limit Reached</strong>
            </div>
            <p style={{ margin: 0, color: '#7F1D1D', fontSize: '14px' }}>
              You've reached your limit of {propertyLimits.maxProperties} {propertiesLabelLower} on the {propertyLimits.planName} plan. 
              You cannot create new {propertiesLabelLower} until you upgrade your plan.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              let plan = '';
              if (propertyLimits.maxProperties <= 3) {
                plan = 'passport';
              } else if (propertyLimits.maxProperties <= 10) {
                plan = 'visa';
              }
              navigate(`/host-checkout${plan ? `?plan=${plan}` : ''}`);
            }}
            style={{
              backgroundColor: '#dc1d08',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'opacity 0.2s',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            Upgrade Plan
          </button>
        </div>
      )}
      {saveError && (
        <div style={{
          backgroundColor: '#FEF2F2',
          border: '1px solid #FCA5A5',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <span style={{ fontSize: '16px' }}>⚠️</span>
              <strong style={{ color: '#DC2626' }}>Error Saving {propertyLabel}</strong>
            </div>
            <p style={{ margin: 0, color: '#7F1D1D', fontSize: '14px' }}>
              {saveError}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setSaveError(null)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#DC2626',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '0 8px',
              fontWeight: 'bold'
            }}
            title="Dismiss error"
          >
            ×
          </button>
        </div>
      )}
      <form className="property-editor__form" onSubmit={handleSave}>
        <div className="property-editor__header">
          <h1 className="property-editor__title">
            {propertyId ? `Edit ${propertyLabel}` : `Add New ${propertyLabel}`}
          </h1>
          <div className="property-editor__actions">
            <button
              type="button"
              className="property-editor__cancel"
              onClick={() => navigate(`/property-management?email=${encodeURIComponent(email)}`)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="property-editor__save"
              disabled={saving || imageUploading || (showLimitWarning && !propertyId)}
              title={imageUploading ? 'Please wait for image upload to finish' : (showLimitWarning && !propertyId ? `You've reached your ${propertiesLabelLower} limit. Upgrade your plan to add more ${propertiesLabelLower}.` : '')}
            >
              {saving ? 'Saving...' : imageUploading ? 'Uploading Image...' : `Save ${propertyLabel}`}
            </button>
          </div>
        </div>

        <div className="property-editor__content">
          <div className="property-editor__main">
            <section className="property-editor__section">
              <h2>Basic Information</h2>
              
              <div className="property-editor__field">
                <label>{propertyLabel} Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    // Clear error when user starts typing
                    if (saveError) setSaveError(null);
                  }}
                  placeholder={`e.g., ${hostCategory === 'tourism_board' ? 'Beautiful Beach Town' : 'Sunset Beach House'}`}
                  required
                />
              </div>

              <div className="property-editor__field">
                <label>{propertyLabel} Handle</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: '#9CA3AF', fontWeight: 600 }}>@</span>
                  <input
                    type="text"
                    value={propertyHandle}
                    onChange={(e) => {
                      setPropertyHandle(e.target.value.replace(/[^a-zA-Z0-9._-]/g, ''));
                      // Clear error when user starts typing
                      if (saveError) setSaveError(null);
                    }}
                    placeholder="yourproperty"
                  />
                </div>
                <small style={{ color: '#6B7280' }}>
                  You can reuse the same Instagram-style handle across multiple {propertiesLabelLower}. Letters, numbers, periods, hyphens and underscores only.
                </small>
              </div>

              <div className="property-editor__field">
                <label>Description</label>
                <textarea
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    // Clear error when user starts typing
                    if (saveError) setSaveError(null);
                  }}
                  placeholder="Describe your property..."
                  rows={4}
                />
              </div>

              <div className="property-editor__row">
                <div className="property-editor__field">
                  <label>Status</label>
                  <select value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>

                <div className="property-editor__field">
                  <label>{propertyLabel} Type</label>
                  <select value={propertyType} onChange={(e) => setPropertyType(e.target.value)}>
                    {PROPERTY_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="property-editor__field property-editor__field--checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={featured}
                      onChange={(e) => setFeatured(e.target.checked)}
                    />
                    Featured {propertyLabel}
                  </label>
                </div>
              </div>
            </section>

            <section className="property-editor__section">
              <h2>Location</h2>
              
              <div className="property-editor__row">
                <div className="property-editor__field">
                  <label>Country</label>
                  <LocationAutocomplete
                    type="country"
                    value={locationCountry}
                    onChange={(value) => {
                      setLocationCountry(value);
                      if (value !== locationCountry) {
                        setLocationState("");
                        setLocationCity("");
                      }
                    }}
                    placeholder="Start typing country..."
                  />
                </div>
              </div>

              <div className="property-editor__row">
                <div className="property-editor__field">
                  <label>State</label>
                  <LocationAutocomplete
                    type="state"
                    value={locationState}
                    onChange={setLocationState}
                    placeholder={locationCountry === "United States" ? "Start typing state..." : "State (optional)"}
                    disabled={locationCountry && locationCountry !== "United States"}
                  />
                </div>

                <div className="property-editor__field">
                  <label>City</label>
                  <LocationAutocomplete
                    type="city"
                    value={locationCity}
                    onChange={setLocationCity}
                    stateValue={locationState}
                    placeholder={locationState ? "Start typing city..." : locationCountry === "United States" ? "Select state first" : "Start typing city..."}
                    disabled={locationCountry === "United States" && !locationState}
                  />
                </div>
              </div>

              <div className="property-editor__row">
                <div className="property-editor__field">
                  <label>Nearest Airport</label>
                  <input
                    type="text"
                    value={nearestAirport}
                    onChange={(e) => setNearestAirport(e.target.value)}
                    placeholder="e.g., LAX"
                  />
                </div>

                <div className="property-editor__field">
                  <label>Distance (miles)</label>
                  <input
                    type="number"
                    value={drivingDistanceMiles}
                    onChange={(e) => setDrivingDistanceMiles(e.target.value)}
                    placeholder="e.g., 25"
                  />
                </div>

                <div className="property-editor__field">
                  <label>Drive Time (minutes)</label>
                  <input
                    type="number"
                    value={drivingTimeMinutes}
                    onChange={(e) => setDrivingTimeMinutes(e.target.value)}
                    placeholder="e.g., 35"
                  />
                </div>
              </div>
            </section>

            {hostCategory === 'vacation_rental' && (
              <section className="property-editor__section">
                <h2>{propertyLabel} Details</h2>
                
                <div className="property-editor__row">
                  <div className="property-editor__field">
                    <label>Bedrooms</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={bedrooms}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setBedrooms(v);
                      }}
                      placeholder="0"
                      maxLength={5}
                    />
                  </div>

                  <div className="property-editor__field">
                    <label>Bathrooms</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={bathrooms}
                      onChange={(e) => {
                        let v = e.target.value.replace(',', '.');
                        v = v.replace(/[^\d.]/g, '');
                        const parts = v.split('.');
                        if (parts.length > 2) {
                          v = `${parts[0]}.${parts[1]}`;
                        }
                        if (v.includes('.')) {
                          const [i, d = ''] = v.split('.');
                          const intPart = i.replace(/\D/g, '').slice(0, 3);
                          const decPart = d.replace(/\D/g, '').slice(0, 1);
                          v = `${intPart}.${decPart}`;
                        } else {
                          v = v.replace(/\D/g, '').slice(0, 3);
                        }
                        if (/^\d+/.test(v)) {
                          const intVal = parseInt(v.split('.')[0] || '0', 10);
                          if (intVal >= 1000) {
                            v = '999.9';
                          }
                        }
                        setBathrooms(v);
                      }}
                      placeholder="0"
                      maxLength={5}
                    />
                  </div>

                  <div className="property-editor__field">
                    <label>Max Guests</label>
                    <input
                      type="number"
                      value={maxGuests}
                      onChange={(e) => setMaxGuests(e.target.value)}
                      min="1"
                      placeholder="1"
                    />
                  </div>

                  <div className="property-editor__field">
                    <label>Property Sleeps</label>
                    <input
                      type="number"
                      value={sleeps}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setSleeps(v);
                      }}
                      min="0"
                      placeholder="0"
                      maxLength={5}
                    />
                  </div>

                  <div className="property-editor__field">
                    <label>Square Footage</label>
                    <input
                      type="number"
                      value={squareFootage}
                      onChange={(e) => setSquareFootage(e.target.value)}
                      min="0"
                      placeholder="0"
                    />
                  </div>
                </div>
              </section>
            )}

            {hostCategory !== 'tourism_board' && (
              <section className="property-editor__section">
                <h2>Amenities</h2>
                
                <div className="property-editor__amenities">
                  {AMENITIES.map(amenity => (
                    <label key={amenity.key} className="property-editor__amenity">
                      <input
                        type="checkbox"
                        checked={amenities.includes(amenity.key)}
                        onChange={() => handleAmenityToggle(amenity.key)}
                      />
                      {amenity.label}
                    </label>
                  ))}
                </div>
              </section>
            )}


            {hostCategory !== 'tourism_board' && (
            <section className="property-editor__section">
              <h2>Listing URLs</h2>
              
              <div className="property-editor__field">
                <label>Direct Booking URL</label>
                <input
                  type="url"
                  value={directBookingUrl}
                  onChange={(e) => setDirectBookingUrl(e.target.value)}
                  placeholder="https://..."
                />
              </div>

              {hostCategory !== 'tourism_board' && (
              <div className="property-editor__field">
                <label>Airbnb URL</label>
                <input
                  type="url"
                  value={airbnbUrl}
                  onChange={(e) => setAirbnbUrl(e.target.value)}
                  placeholder="https://airbnb.com/..."
                />
              </div>
              )}

              {hostCategory !== 'tourism_board' && (
              <div className="property-editor__field">
                <label>VRBO URL</label>
                <input
                  type="url"
                  value={vrboUrl}
                  onChange={(e) => setVrboUrl(e.target.value)}
                  placeholder="https://vrbo.com/..."
                />
              </div>
              )}

              {hostCategory !== 'tourism_board' && (
              <div className="property-editor__field">
                <label>Expedia URL</label>
                <input
                  type="url"
                  value={expediaUrl}
                  onChange={(e) => setExpediaUrl(e.target.value)}
                  placeholder="https://expedia.com/..."
                />
              </div>
              )}

              {hostCategory !== 'tourism_board' && (
              <div className="property-editor__field">
                <label>Other Listing URL</label>
                <input
                  type="url"
                  value={otherListingUrl}
                  onChange={(e) => setOtherListingUrl(e.target.value)}
                  placeholder="https://..."
                />
              </div>
              )}
            </section>
            )}

            <section className="property-editor__section">
              <h2>Social Media</h2>

              <div className="property-editor__field">
                <label>Instagram URL</label>
                <input
                  type="url"
                  value={igUrl}
                  onChange={(e) => setIgUrl(e.target.value)}
                  placeholder="https://instagram.com/yourhandle"
                />
              </div>

              <div className="property-editor__field">
                <label>TikTok URL</label>
                <input
                  type="url"
                  value={ttUrl}
                  onChange={(e) => setTtUrl(e.target.value)}
                  placeholder="https://tiktok.com/@yourhandle"
                />
              </div>

              <div className="property-editor__field">
                <label>YouTube URL</label>
                <input
                  type="url"
                  value={ytUrl}
                  onChange={(e) => setYtUrl(e.target.value)}
                  placeholder="https://youtube.com/@yourchannel"
                />
              </div>

              {hostCategory !== 'tourism_board' && hostCategory !== 'boutique_hotel' && (
              <div className="property-editor__field">
                <label>LTK URL</label>
                <input
                  type="url"
                  value={ltkUrl}
                  onChange={(e) => setLtkUrl(e.target.value)}
                  placeholder="https://www.shopltk.com/explore/yourhandle"
                />
              </div>
              )}
            </section>
          </div>

          <div className="property-editor__sidebar">
            <section className="property-editor__section">
              <h2>{propertyLabel} Image</h2>
              
              <div className="property-editor__image-upload">
                {imageUploading ? (
                  <div className="property-editor__image-dropzone" style={{ minHeight: '200px' }}>
                    <span style={{ fontSize: '16px', fontWeight: 600, color: '#dc1d08' }}>
                      Uploading and converting image...
                    </span>
                    <small>This may take up to 30 seconds for HEIC files</small>
                  </div>
                ) : imageUploadSuccess ? (
                  <div className="property-editor__image-dropzone" style={{ 
                    minHeight: '200px',
                    borderColor: '#10b981',
                    background: 'rgba(16, 185, 129, 0.05)'
                  }}>
                    <span style={{ fontSize: '24px', color: '#10b981' }}>✓</span>
                    <span style={{ fontSize: '16px', fontWeight: 600, color: '#10b981' }}>
                      Image uploaded successfully!
                    </span>
                  </div>
                ) : imagePreview ? (
                  <div className="property-editor__image-preview">
                    <img src={imagePreview} alt={`${propertyLabel} preview`} />
                    <button
                      type="button"
                      className="property-editor__image-remove"
                      onClick={() => {
                        setImageFile(null);
                        setImageUrl('');
                        setImagePreview('');
                      }}
                    >
                      Remove Image
                    </button>
                  </div>
                ) : (
                  <label 
                    className="property-editor__image-dropzone"
                    onClick={(e) => {
                      e.preventDefault();
                      triggerFileInput();
                    }}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,.heic,.heif"
                      onChange={handleImageChange}
                      style={{ display: 'none' }}
                    />
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M12 4v16m-8-8h16" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    <span>Click to upload image (jpeg or png)</span>
                    <small>Recommended: 1200x800px</small>
                  </label>
                )}
              </div>
            </section>

            <section className="property-editor__section">
              <h2>{propertyLabel} Intro Video</h2>
              {propertyId ? (
                <>
                  <IntroVideoUpload
                    kind="property_intro_video"
                    propertyId={propertyId}
                    value={introVideo}
                    onChange={handleIntroVideoChange}
                    title={`Add a 30-second intro video for this ${propertyLabelLower}`}
                    description="Plays muted on hover across discovery surfaces and on this property's page."
                  />
                  {introVideoStatus && (
                    <div style={{
                      marginTop: 8,
                      fontSize: 13,
                      color: introVideoStatus.startsWith('Could') ? '#dc1d08' : '#0f084b',
                      textAlign: 'center'
                    }}>
                      {introVideoStatus}
                    </div>
                  )}
                </>
              ) : (
                <p style={{ fontSize: 13, color: '#777E90', margin: 0 }}>
                  Save this {propertyLabelLower} first, then return here to add a 30-second intro video.
                </p>
              )}
            </section>

            <section className="property-editor__section">
              <h2>{propertyLabel} Showcase</h2>
              {propertyId ? (
                <ShowcaseUpload
                  mode="dashboard"
                  profileKind="property"
                  profileId={propertyId}
                  value={showcaseAssets}
                  onChange={setShowcaseAssets}
                  heading={`${propertyLabel} showcase`}
                  copy={`Add up to 3 photos or short-form reels of this ${propertyLabelLower}.`}
                />
              ) : (
                <p style={{ fontSize: 13, color: '#777E90', margin: 0 }}>
                  Save this {propertyLabelLower} first, then return here to add up to 3 photos or reels.
                </p>
              )}
            </section>

            <section className="property-editor__section">
              <h2>Quick Actions</h2>
              
              <button
                type="button"
                className="property-editor__quick-action"
                onClick={() => setStatus(status === 'active' ? 'inactive' : 'active')}
              >
                {status === 'active' ? `Deactivate ${propertyLabel}` : `Activate ${propertyLabel}`}
              </button>
              
              <button
                type="button"
                className="property-editor__quick-action"
                onClick={() => setFeatured(!featured)}
              >
                {featured ? 'Remove from Featured' : 'Mark as Featured'}
              </button>
              
              {propertyId && (
                <button
                  type="button"
                  className="property-editor__quick-action property-editor__quick-action--danger"
                  onClick={() => {
                    if (window.confirm(`Are you sure you want to delete this ${propertiesLabelLower.slice(0, -1)}?`)) {
                      // Handle deletion
                    }
                  }}
                >
                  Delete {propertyLabel}
                </button>
              )}
            </section>
          </div>
        </div>
      </form>
    </div>
    </div>
  );

  return (
    <InfoPage
      title={propertyId ? `Edit ${propertyLabel}` : `Add New ${propertyLabel}`}
      below={editorContent}
      tallOverride={false}
      customMinHeight={800}
      disableStamps
    />
  );
};

export default PropertyEditor;
